import React, { useState } from 'react';
import { getDateRangeFromToday } from 'src/utils/timeUtils';
import {
  Button,
  DateRangePickerProps as CSDateRangePickerProps,
  DateRangePicker as CSDateRangePicker,
  FormField,
} from '@cloudscape-design/components';

export interface DateRangePickerProps {
  label?: string;
  initDateRange?: { startDate: Date; endDate: Date };
  changeDateRange: (arg: DateRange) => void;
  setSearchParams?: (arg: Record<string, string>) => void;
}

export function DateRangePicker({
  label,
  initDateRange = getDateRangeFromToday(7),
  changeDateRange,
  setSearchParams,
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState<Date>(initDateRange.startDate);
  const [endDate, setEndDate] = useState<Date>(initDateRange.endDate);

  const isDateRangeValid = (startDate: Date, endDate: Date) =>
    startDate <= endDate;

  const submit = (dateRange: DateRange) => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);

    if (isDateRangeValid(dateRange.startDate, dateRange.endDate)) {
      setSearchParams?.({
        startDate: dateRange.startDate.toJSON(),
        endDate: dateRange.endDate.toJSON(),
      });
      changeDateRange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  };

  // Datepicker
  const [value, setValue] = React.useState<
    CSDateRangePickerProps.Value | undefined
  >({ key: 'previous-1-month', amount: 30, unit: 'day', type: 'relative' });

  return (
    <div className="flex items-end gap-2 mb-4">
      <FormField label={label ?? 'Date Range'}>
        <CSDateRangePicker
          onChange={({ detail }) => {
            setValue(detail.value ?? undefined);
            if (detail.value?.type === 'absolute') {
              setStartDate(new Date(detail.value.startDate));
              setEndDate(new Date(detail.value.endDate));
            }
            if (detail.value?.type === 'relative') {
              // assumes options in days
              const range = getDateRangeFromToday(detail.value.amount);
              setStartDate(range.startDate);
              setEndDate(range.endDate);
            }
          }}
          customRelativeRangeUnits={['day']}
          value={value ?? null}
          relativeOptions={[
            {
              key: 'previous-1-day',
              amount: 1,
              unit: 'day',
              type: 'relative',
            },
            {
              key: 'previous-7-days',
              amount: 7,
              unit: 'day',
              type: 'relative',
            },
            {
              key: 'previous-1-month',
              amount: 30,
              unit: 'day',
              type: 'relative',
            },
          ]}
          isValidRange={(range) => {
            if (range?.type === 'absolute') {
              const [startDateWithoutTime] = range.startDate.split('T');
              const [endDateWithoutTime] = range.endDate.split('T');
              if (!startDateWithoutTime || !endDateWithoutTime) {
                return {
                  valid: false,
                  errorMessage:
                    'The selected date range is incomplete. Select a start and end date for the date range.',
                };
              }
              if (range) {
                const start = new Date(range.startDate);
                const end = new Date(range.endDate);
                // @ts-expect-error
                if (start - end > 0) {
                  return {
                    valid: false,
                    errorMessage:
                      'The selected date range is invalid. The start date must be before the end date.',
                  };
                }
              }
            }
            return { valid: true };
          }}
          placeholder="Filter by a date range"
        />
      </FormField>
      <Button
        variant="primary"
        disabled={!isDateRangeValid(startDate, endDate)}
        onClick={() => submit({ startDate, endDate })}
      >
        Search
      </Button>
    </div>
  );
}
